import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Link,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  VerifiedUser as LicenseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {useNavigate} from 'react-router-dom';

interface LicenseQuotaDisplayProps {
  variant?: 'compact' | 'detailed' | 'header-card';
  showAlert?: boolean;
}

export default function LicenseQuotaDisplay({
  variant = 'compact',
  showAlert = false,
}: LicenseQuotaDisplayProps) {
  const navigate = useNavigate();
  const {
    quota,
    loading,
    remainingUnits,
    usagePercentage,
    isNearLimit,
    isAtLimit,
    totalUnits,
    usedUnits,
    refetch,
    hasSipLicense,
    sipTotalUnits,
    sipUsedUnits,
    sipAvailableUnits,
    sipHasUnlimited,
    sipUsagePercentage,
  } = useLicenseQuota();

  // SIP licenses cap users rather than units, so they get their own row rather
  // than being folded into the unit totals above.
  const sipSummary = sipHasUnlimited
    ? `${sipUsedUnits} used / Unlimited`
    : `${sipUsedUnits}/${sipTotalUnits}`;

  if (loading || !quota) {
    return variant === 'compact' ? (
      <Chip
        size="small"
        icon={<LicenseIcon />}
        label="Loading quota..."
        sx={{bgcolor: '#F3F4F6', color: '#6B7280'}}
      />
    ) : null;
  }

  const getStatusColor = () => {
    if (isAtLimit) return '#DC2626';
    if (isNearLimit) return '#D97706';
    return '#059669';
  };

  const getStatusIcon = () => {
    if (isAtLimit) return <ErrorIcon fontSize="small" />;
    if (isNearLimit) return <WarningIcon fontSize="small" />;
    return <LicenseIcon fontSize="small" />;
  };

  if (variant === 'header-card') {
    return (
      <Card
        sx={{
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          bgcolor: 'white',
          minWidth: 260,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderColor: '#D1D5DB',
          },
        }}
        onClick={() => navigate('/settings/license')}
      >
        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mr: 3}}>
              <LicenseIcon sx={{fontSize: 18, color: '#2D68FE'}} />
              <Typography
                variant="body2"
                sx={{color: '#111827', fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap'}}
              >
                License Usage
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{color: '#2D68FE', fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap'}}
            >
              {usedUnits} / {totalUnits} Units Used
            </Typography>
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <LinearProgress
              variant="determinate"
              value={Math.min(usagePercentage, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#F1F5F9',
                flex: 1,
                '& .MuiLinearProgress-bar': {
                  bgcolor: getStatusColor(),
                  borderRadius: 4,
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{color: getStatusColor(), fontWeight: 700, fontSize: '0.875rem', minWidth: 36}}
            >
              {Math.round(usagePercentage)}%
            </Typography>
          </Box>
          {hasSipLicense && (
            <Box sx={{mt: 1.5, pt: 1.5, borderTop: '1px solid #E5E7EB'}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{color: '#6B7280', fontSize: '0.7rem', fontWeight: 500}}
                >
                  SIP Quota
                </Typography>
                <Typography variant="caption" sx={{color: '#6B7280', fontSize: '0.7rem'}}>
                  {sipSummary}
                </Typography>
              </Box>
              {!sipHasUnlimited && (
                <LinearProgress
                  variant="determinate"
                  value={Math.min(sipUsagePercentage, 100)}
                  sx={{
                    height: 2,
                    borderRadius: 2,
                    bgcolor: '#F1F5F9',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#3B82F6',
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  if (variant === 'compact') {
    return (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Tooltip
          title={
            <Box>
              <Typography variant="body2" sx={{fontWeight: 600, mb: 1}}>
                License Usage
              </Typography>
              <Typography variant="body2">
                Used: {usedUnits} / {totalUnits} units
              </Typography>
              <Typography variant="body2">Available: {remainingUnits} units</Typography>
              {hasSipLicense && (
                <Typography variant="body2" sx={{mt: 0.5}}>
                  SIP: {sipSummary}
                  {!sipHasUnlimited && ` (${sipAvailableUnits} available)`}
                </Typography>
              )}
              <Typography variant="body2" sx={{mt: 1, color: '#93C5FD'}}>
                Click to manage licenses
              </Typography>
            </Box>
          }
          arrow
        >
          <Chip
            size="small"
            icon={getStatusIcon()}
            label={`${remainingUnits} units left`}
            onClick={() => navigate('/settings/license')}
            sx={{
              bgcolor: isAtLimit ? '#FEF2F2' : isNearLimit ? '#FFFBEB' : '#F0FDF4',
              color: getStatusColor(),
              cursor: 'pointer',
              '&:hover': {
                bgcolor: isAtLimit ? '#FCA5A5' : isNearLimit ? '#FDE68A' : '#BBF7D0',
              },
            }}
          />
        </Tooltip>
        <Tooltip title="Refresh quota">
          <IconButton size="small" onClick={refetch} sx={{color: '#6B7280'}}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      }}
    >
      <CardContent sx={{p: 3}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            {getStatusIcon()}
            <Typography variant="h6" sx={{color: '#111827', fontWeight: 600}}>
              License Usage
            </Typography>
          </Box>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/settings/license')}
            sx={{
              color: '#3B82F6',
              textDecoration: 'none',
              '&:hover': {textDecoration: 'underline'},
            }}
          >
            Manage Licenses
          </Link>
        </Box>

        <Box sx={{mb: 2}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
            <Typography variant="body2" sx={{color: '#6B7280'}}>
              {usedUnits} of {totalUnits} units used
            </Typography>
            <Typography variant="body2" sx={{color: getStatusColor(), fontWeight: 600}}>
              {Math.round(usagePercentage)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(usagePercentage, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: '#F1F5F9',
              '& .MuiLinearProgress-bar': {
                bgcolor: getStatusColor(),
                borderRadius: 3,
              },
            }}
          />
          <Typography variant="body2" sx={{color: '#6B7280', mt: 1, textAlign: 'center'}}>
            {remainingUnits} units remaining
          </Typography>
        </Box>

        {hasSipLicense && (
          <Box sx={{mb: 2, pt: 2, borderTop: '1px solid #E5E7EB'}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
              <Typography variant="body2" sx={{color: '#6B7280', fontWeight: 500}}>
                SIP Quota
              </Typography>
              {!sipHasUnlimited && (
                <Typography variant="body2" sx={{color: '#3B82F6', fontWeight: 600}}>
                  {Math.round(sipUsagePercentage)}%
                </Typography>
              )}
            </Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
              <Typography variant="body2" sx={{color: '#6B7280'}}>
                {sipHasUnlimited
                  ? `${sipUsedUnits} users on SIP`
                  : `${sipUsedUnits} of ${sipTotalUnits} users on SIP`}
              </Typography>
            </Box>
            {!sipHasUnlimited && (
              <LinearProgress
                variant="determinate"
                value={Math.min(sipUsagePercentage, 100)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#F1F5F9',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#3B82F6',
                    borderRadius: 3,
                  },
                }}
              />
            )}
            <Typography variant="body2" sx={{color: '#6B7280', mt: 1, textAlign: 'center'}}>
              {sipHasUnlimited
                ? 'Unlimited SIP licenses'
                : `${sipAvailableUnits} SIP licenses remaining`}
            </Typography>
          </Box>
        )}

        {showAlert && isNearLimit && (
          <Alert
            severity={isAtLimit ? 'error' : 'warning'}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {fontSize: '0.875rem'},
            }}
          >
            {isAtLimit
              ? 'No units available. Add more licenses to continue adding units.'
              : `Only ${remainingUnits} units left. Consider adding more licenses soon.`}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
